<script setup lang="ts">
import { computed } from 'vue';
import type { CategoryStatDto } from '@/api/reports';

interface Props {
  categories: CategoryStatDto[];
}

const props = defineProps<Props>();

const sorted = computed(() =>
  [...props.categories].sort((a, b) => b.count - a.count),
);

const maxCount = computed(() =>
  Math.max(1, ...sorted.value.map(c => c.count)),
);

function barWidth(count: number): string {
  return `${(count / maxCount.value) * 100}%`;
}

function deltaLabel(pct: number): string {
  if (Math.abs(pct) < 0.5) return '—';
  return pct > 0 ? `▲${pct.toFixed(0)}%` : `▼${Math.abs(pct).toFixed(0)}%`;
}

function deltaClass(pct: number): string {
  if (Math.abs(pct) < 0.5) return 'text-muted';
  return pct > 0 ? 'text-red-600' : 'text-green-700';
}

// Category → color: matches the PDF generator CategoryColor mapping
const categoryColors: Record<string, string> = {
  gate:         'bg-red-500',
  'gate access': 'bg-red-500',
  safety:       'bg-orange-500',
  parking:      'bg-amber-500',
  pool:         'bg-navy-500',
  'pool & rec': 'bg-navy-500',
  maintenance:  'bg-violet-500',
  landscape:    'bg-green-600',
};

function barClass(category: string): string {
  return categoryColors[category.toLowerCase()] ?? 'bg-gray-400';
}
</script>

<template>
  <div data-testid="report-category-breakdown">
    <div v-if="categories.length === 0" class="text-sm text-muted py-4">
      No issues recorded this period.
    </div>

    <div v-else class="space-y-3">
      <div
        v-for="cat in sorted"
        :key="cat.category"
        class="flex items-center gap-3"
      >
        <!-- Category name -->
        <span class="w-28 shrink-0 text-sm font-medium text-fg truncate">
          {{ cat.category }}
        </span>
        <!-- Bar -->
        <div class="flex-1 h-2 bg-canvas-soft rounded-full overflow-hidden">
          <div
            :class="barClass(cat.category)"
            class="h-full rounded-full transition-all duration-300"
            :style="{ width: barWidth(cat.count) }"
          />
        </div>
        <!-- Count -->
        <span class="w-8 text-right font-mono tabular-nums text-sm text-fg shrink-0">
          {{ cat.count }}
        </span>
        <!-- MoM delta -->
        <span
          class="w-12 text-right font-mono tabular-nums text-xs shrink-0"
          :class="deltaClass(cat.vsPriorMonthPct)"
        >
          {{ deltaLabel(cat.vsPriorMonthPct) }}
        </span>
      </div>
    </div>
  </div>
</template>
